/**
 * Where the Hub lives.
 *
 * Three branches, mutually exclusive:
 *
 * 1. **Tauri desktop**: `__TAURI_INTERNALS__` is present. Per ADR 0013 the
 *    desktop opens no TCP listener; HTTP rides a `fabricos://localhost`
 *    custom URI scheme handled in-process by `apps/desktop/src-tauri/src/main.rs`,
 *    and WebSocket is replaced entirely by Tauri events (see useHubSocket).
 * 2. **Cloud web** (`fabricos.pages.dev`): `VITE_HUB_PROXY` is baked in at
 *    build time and points at the public cloud Hub URL. The Hub's CORS layer
 *    allows it.
 * 3. **Vite dev** (browser): no env var; relative paths flow through the
 *    Vite proxy defined in `vite.config.ts`.
 */

export const IS_TAURI =
  typeof window !== 'undefined' && '__TAURI_INTERNALS__' in window;

/**
 * Tauri 2 maps custom URI schemes per platform:
 *   - macOS, Linux, iOS         → `fabricos://localhost`
 *   - Windows, Android          → `http://fabricos.localhost`
 *
 * The split exists because Chromium-based WebViews (Edge WebView2 on
 * Windows, WebView on Android) reject non-http/https schemes inside the
 * fetch() API. Using `http://<scheme>.localhost` lets fetch through; Tauri
 * still intercepts the request via its registered scheme protocol handler.
 */
function pickTauriHubBase(): string {
  if (typeof navigator === 'undefined') return 'fabricos://localhost';
  const ua = navigator.userAgent;
  if (/Android|Windows/i.test(ua)) return 'http://fabricos.localhost';
  return 'fabricos://localhost';
}

const TAURI_HUB_BASE = pickTauriHubBase();

const RAW = IS_TAURI
  ? TAURI_HUB_BASE
  : ((import.meta as { env?: { VITE_HUB_PROXY?: string } }).env?.VITE_HUB_PROXY ?? '').replace(
      /\/$/,
      '',
    );

export const HUB_HTTP_BASE = RAW;

/**
 * Base for WebSocket connections. **Empty when running under Tauri** because
 * embedded mode uses Tauri events instead of a real WS — useHubSocket reads
 * `IS_TAURI` to branch transport.
 */
export const HUB_WS_BASE = (() => {
  if (IS_TAURI) return '';
  if (RAW) {
    return RAW.replace(/^https/, 'wss').replace(/^http/, 'ws');
  }
  if (typeof window === 'undefined') return '';
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}`;
})();

const IS_ANDROID_TAURI =
  IS_TAURI && typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent);

/** `fetch` shim that auto-prepends `HUB_HTTP_BASE` to relative paths.
 *
 * On Android-Tauri this routes through the `hub_http` Tauri command instead
 * of `fetch()` — Android's WebView drops POST bodies during
 * `shouldInterceptRequest`, breaking any JSON request through the custom
 * URI scheme. The invoke bridge ferries (method, path, headers, body) to
 * the same embedded axum router and rebuilds a `Response` object so
 * callers see the same shape. */
export async function hubFetch(path: string, init?: RequestInit): Promise<Response> {
  if (IS_ANDROID_TAURI) {
    return hubFetchViaInvoke(path, init);
  }
  return fetch(`${HUB_HTTP_BASE}${path}`, init);
}

async function hubFetchViaInvoke(path: string, init?: RequestInit): Promise<Response> {
  const { invoke } = await import('@tauri-apps/api/core');
  const method = (init?.method ?? 'GET').toUpperCase();
  let body = '';
  if (init?.body != null) {
    if (typeof init.body === 'string') {
      body = init.body;
    } else {
      // ReadableStream / Blob / FormData / URLSearchParams — read as text.
      body = await new Response(init.body as BodyInit).text();
    }
  }
  const headers: Record<string, string> = {};
  if (init?.headers) {
    if (init.headers instanceof Headers) {
      init.headers.forEach((v, k) => {
        headers[k] = v;
      });
    } else if (Array.isArray(init.headers)) {
      for (const [k, v] of init.headers) headers[k] = v;
    } else {
      Object.assign(headers, init.headers);
    }
  }
  const result = await invoke<{
    status: number;
    headers: Record<string, string>;
    body: string;
  }>('hub_http', { method, path, headers, body });
  return new Response(result.body, {
    status: result.status,
    headers: result.headers,
  });
}
